<template>
	<Form as=""
		:validation-schema="formSchema"
		v-slot="{ errors, validate, meta }">

		<v-form data-test="form"
			@submit.prevent="validateAddressForm(validate)">

			<v-row class="ma-0">
				<!-- street field -->
				<v-col :class="getFieldPadding(errors.hasOwnProperty('street'))"
					cols="12">

					<Field
						v-model="formFields.street.value"
						:name="formFields.street.name"
						:rules="formFields.street.rules"
						validateOnInput
						v-slot="{ field }"
					>
						<VTextField
							ref="streetField"
							data-test="street"
							v-bind="field"
							:label="formFields.street.label"
							:max-length="formFields.street.maxLength"
							:counter="
								(formFields.street.value?.length || 0) > formFields.street.maxLength - 5
							"
							:helperText="errors.street"
							:invalid="errors.hasOwnProperty('street')"
							aria-labelledby="body-info"
						>
						</VTextField>
					</Field>
				</v-col>

				<!-- city field -->
				<v-col :class="getFieldPadding(errors.hasOwnProperty('city'))"
					cols="12">
					<Field
						v-model="formFields.city.value"
						:name="formFields.city.name"
						:rules="formFields.city.rules"
						validateOnInput
						v-slot="{ field }"
					>
						<VTextField
							data-test="city"
							v-bind="field"
							:label="formFields.city.label"
							:max-length="formFields.city.maxLength"
							:counter="
								(formFields.city.value?.length || 0) > formFields.city.maxLength - 5
							"
							:helperText="errors.city"
							:invalid="errors.hasOwnProperty('city')"
							aria-labelledby="body-info"
						>
						</VTextField>
					</Field>
				</v-col>

				<!-- zip field -->
				<v-col :class="getFieldPadding(errors.hasOwnProperty('zip'))"
					cols="12">
					<Field
						v-model="formFields.zip.value"
						:name="formFields.zip.name"
						:rules="formFields.zip.rules + formFields.country.value"
						validateOnInput
						v-slot="{ field }"
					>
						<VTextField
							data-test="zip"
							v-bind="field"
							:label="formFields.zip.label"
							:max-length="formFields.zip.maxLength"
							:counter="formFields.zip.maxLength > 0"
							:helperText="errors.zip"
							:invalid="errors.hasOwnProperty('zip')"
							aria-labelledby="body-info"
						>
						</VTextField>
					</Field>
				</v-col>

				<!-- country select -->
				<v-col :class="getFieldPadding(formFields.country)"
					cols="12">

					<scale-dropdown-select 
						data-test="country"
						:value="formFields.country.value"
						:label="formFields.country.label"
						@scale-change="formFields.country.value = $event.target.value">

						<scale-dropdown-select-item v-for="(country, index) in countries"
							:key="index"
							:value="country.value"
							:selected="country.value === formFields.country.value">{{ country.label }}</scale-dropdown-select-item>
					</scale-dropdown-select>
				</v-col>
			</v-row>

			<!-- FORM ACTION BUTTON-->
			<v-row class="ma-0 mt-8 d-flex"
				justify="end">
				<scale-button
					type="submit"
					:disabled="props.loading || !meta.valid"
				>
					<!-- Loading spinner -->
					<scale-loading-spinner
						v-if="loading"
						size="small"
					></scale-loading-spinner>
					<span v-else>
						{{ t("buttons.primaryBtn.addressFormScreen") }}
					</span>
				</scale-button>
			</v-row>
		</v-form>
	</Form>
</template>

<script setup>
  import { ref, watch, computed, reactive, onMounted, defineProps, inject, defineEmits } from 'vue';


  // Components
  import { Form, Field } from 'vee-validate';
  import VTextField from './VTextField.vue';


  // Services
  import RegistrationAPI from '../services/RegistrationAPI';


  // Plugins
  import { useI18n } from 'vue-i18n';
  import isoCountries from '@/plugins/vue-i18n';

  // Props
  const props = defineProps({
    loading: {
      type: Boolean,
      required: true,
    },
  });


  // Emits
  const emit = defineEmits(['form-submit', 'show-notification']);


  // Reusable data
  const { t, locale } = useI18n();
  const keycloak = inject('keycloak');

  const isFormValid = ref(true);
  const formFields = reactive({
    street: {
      name: "street",
      label: t('address.street'),
      value: '',
      errorMsg: '',
      rules: 'required|max:60',
      maxLength: 60,
    },
    city: {
      name: "city",
      label: t('address.city'),
      value: '',
      errorMsg: '',
      rules: 'required|max:40',
      maxLength: 40,
    },
    zip: {
      name: "zip",
      label: t('address.zip'),
      value: '',
      errorMsg: '',
      rules: 'required|DE_zip:',
      maxLength: undefined,
    },
    country: {
      name: "country",
      label: t('address.country'),
      value: 'DE',
      errorMsg: '',
    },
  });

  const countries = computed(() => {
    const list = isoCountries.getNames(locale.value, {
      select: "official",
    });
    return Object.keys(list).map((key) => ({ value: key, label: list[key] }));
  });


  // Fetch data
  const getAddressData = () => {
    RegistrationAPI.getAddress(keycloak.token)
      .then((res) => {
        formFields.street.value = res.data?.street;
        formFields.city.value = res.data?.city;
        formFields.zip.value = res.data?.zip;
        formFields.country.value = res.data?.country;
      })
      .catch((err) => {
        emit("show-notification", 'danger',
             t('errors.addressDataError'),
             `${err.response?.data.status || "Unknown status"}: ${err.response?.data.error || "Unknown error"}`);
        console.error('Error getting address -> ', err);
      });
  };

  onMounted(() => {
    getAddressData();
  });


  // Validation
  const formSchema = computed(() => {
    const schema = {};

    Object.keys(formFields).forEach(key => {
      const field = formFields[key];
      if(field.name === "zip") {
        schema[field.name] = field.rules + formFields.country.value;
      } else {
        schema[field.name] = field.rules;
      }
    });

    return schema;
  });

  const validateAddressForm = (validate) => {
    isFormValid.value = false;
    let isCountryValid = formFields.country.value !== '';
    validate().then((valid) => {
      if (valid && isCountryValid) {
        isFormValid.value = true;
        let addressData = {
          street: formFields.street.value,
          city: formFields.city.value,
          zip: formFields.zip.value,
          country: formFields.country.value,
        };
        emit('form-submit', addressData);
      } 
    });
  };

  // Other
  watch(
    () => formFields,
    (newVal) => {
      isFormValid.value = true;
      if (newVal.country.value === 'DE') {
        formFields.zip.maxLength = 5;
      } else {
        formFields.zip.maxLength = undefined;
      }
    },
    { deep: true }
  );

  const getFieldPadding = (isFieldInvalid) => {
    return isFieldInvalid ? 'pa-0 pb-3' : 'pa-0 pb-8';
  };
</script>
